/**
 * RadialMenu — the "Radial Selection Wheel (split arcs)" right-edge control
 * (Option B from BisaWofa-RightControls-Designs).
 *
 * A hub docked to the right edge, vertically centred. Tapping it fans a
 * half-wheel of labelled items into the map:
 *   - the UPPER arc holds "Show existing" items (olive),
 *   - the LOWER arc holds "Create new" items (purple).
 * Tap an item to run its action; tap the hub (now ✕) or the backdrop to close.
 *
 * Config-driven and self-contained (injects its own CSS). It knows nothing
 * about the app's data — the host passes item actions.
 *
 * Usage:
 *   const wheel = new RadialMenu({
 *     target: mapEl,
 *     sections: [
 *       { key:'show',   arc:'upper', color:'#556B2F',
 *         items:[{ id:'saved', icon:'bi-geo-alt', label:'Saved', onSelect(){…} }] },
 *       { key:'create', arc:'lower', color:'#8B008B',
 *         items:[{ id:'add', icon:'bi-plus-lg', label:'Place', onSelect(){…} }] },
 *     ],
 *   });
 *   wheel.setItemActive('record', true);   // e.g. reflect recording state
 *
 * @module components/RadialMenu
 */

const RADIUS = 150;          // px from hub centre to item centre
const HUB_SIZE = 60;
const ITEM_SIZE = 52;

// Arc geometry. Items fan out around 180° (straight left). The angular range is
// FIXED — upper 90°→160°, lower 200°→270° — for two reasons:
//   • the 90° / 270° limits keep every item on the left half of the hub; going
//     past them (θ<90 or θ>270) swings the item to the RIGHT, off the screen
//     edge where the hub is docked;
//   • the 40° gap between 160° and 200° is a dead-zone at 180° so the upper
//     arc's last item and the lower arc's first item never collide.
// To fit more items we grow RADIUS (more arc length per degree), not the angular
// span. At RADIUS 150 the 70° arc comfortably holds 4 items (~61px apart).
function arcRange(arc /*, n */) {
  return arc === 'upper' ? [90, 160] : [200, 270];
}

export class RadialMenu {
  /**
   * @param {object} opts
   * @param {HTMLElement} opts.target   element to append into (e.g. map target)
   * @param {Array} opts.sections       [{ key, arc:'upper'|'lower', color, items:[{id,icon,label,onSelect}] }]
   * @param {string} [opts.hubIcon]     Bootstrap-icon class for the closed hub
   * @param {string} [opts.hubLabel]    aria-label for the hub
   */
  constructor(opts = {}) {
    _ensureStyles();
    this.target = opts.target || document.body;
    this.sections = opts.sections || [];
    this.hubIcon = opts.hubIcon || 'bi-grid';
    this.hubImage = opts.hubImage || null;   // optional image URL for the closed hub
    this.hubLabel = opts.hubLabel || 'Menu';
    this.side = opts.side === 'left' ? 'left' : 'right';   // which edge the hub docks to
    this.open = false;
    this._itemEls = new Map();   // id → element
    this._items = [];            // { el, dx, dy } — dx is the right-side (leftward) offset
    this._build();
  }

  _build() {
    const root = document.createElement('div');
    root.className = 'radial' + (this.side === 'left' ? ' radial-left' : '');
    root.innerHTML = `
      <div class="radial-backdrop"></div>
      <div class="radial-items"></div>
      <button type="button" class="radial-hub${this.hubImage ? ' radial-hub-hasimg' : ''}" aria-label="${_esc(this.hubLabel)}" aria-expanded="false">
        ${this.hubImage
          ? `<img class="radial-hub-open radial-hub-img" src="${_esc(this.hubImage)}" alt="">`
          : `<i class="bi ${_esc(this.hubIcon)} radial-hub-open"></i>`}
        <i class="bi bi-x-lg radial-hub-close"></i>
      </button>
      <div class="radial-caption radial-caption-upper">SHOW</div>
      <div class="radial-caption radial-caption-lower">CREATE</div>
    `;
    this.target.appendChild(root);
    this.root = root;
    this.hub = root.querySelector('.radial-hub');
    this.backdrop = root.querySelector('.radial-backdrop');
    const itemsWrap = root.querySelector('.radial-items');

    // Lay out items along their arc. θ is measured CCW from +x (pointing
    // right); we open left, so items sit around 180°. The arc range is computed
    // from the item count (see arcRange) so an arc can hold 4+ items without
    // collisions. Screen y is down, so dy is negated.
    for (const section of this.sections) {
      const items = section.items || [];
      const [a0, a1] = arcRange(section.arc);
      items.forEach((item, i) => {
        const t = items.length === 1 ? (a0 + a1) / 2
          : a0 + (a1 - a0) * (i / (items.length - 1));
        const rad = (t * Math.PI) / 180;
        const dx = RADIUS * Math.cos(rad);      // negative → left (right-handed default)
        const dy = -RADIUS * Math.sin(rad);     // negative → up
        // Left-handed: dock on the left edge and fan the items rightward by
        // mirroring the horizontal offset across the vertical axis.
        const tx = this.side === 'left' ? -dx : dx;

        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'radial-item';
        el.style.setProperty('--tx', `${tx}px`);
        el.style.setProperty('--ty', `${dy}px`);
        el.style.setProperty('--accent', section.color);
        el.innerHTML =
          `<span class="radial-item-icon"><i class="bi ${_esc(item.icon)}"></i></span>` +
          `<span class="radial-item-label">${_esc(item.label)}</span>`;
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
          try { item.onSelect?.(item); } catch (err) { console.error('[RadialMenu] item action:', err); }
        });
        itemsWrap.appendChild(el);
        this._items.push({ el, dx, dy });   // store the right-side offset for setSide()
        if (item.id) this._itemEls.set(item.id, el);
      });
    }

    this.hub.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(); });
    this.backdrop.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.open) this.close(); });
  }

  toggle() { this.open ? this.close() : this.openMenu(); }

  openMenu() {
    this.open = true;
    this.root.classList.add('radial-open');
    this.hub.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.open = false;
    this.root.classList.remove('radial-open');
    this.hub.setAttribute('aria-expanded', 'false');
  }

  /** Toggle an active/highlighted state on an item (e.g. recording). */
  setItemActive(id, active) {
    this._itemEls.get(id)?.classList.toggle('radial-item-active', !!active);
  }

  /** Update an item's icon + label (e.g. Record → Stop). */
  setItem(id, { icon, label } = {}) {
    const el = this._itemEls.get(id);
    if (!el) return;
    if (icon) el.querySelector('.radial-item-icon i').className = `bi ${icon}`;
    if (label) el.querySelector('.radial-item-label').textContent = label;
  }

  /**
   * Switch the wheel to the left or right edge (handedness). Re-mirrors every
   * item's horizontal offset; no rebuild needed.
   * @param {'left'|'right'} side
   */
  setSide(side) {
    const next = side === 'left' ? 'left' : 'right';
    if (next === this.side) return;
    this.side = next;
    this.root.classList.toggle('radial-left', next === 'left');
    for (const { el, dx } of this._items) {
      el.style.setProperty('--tx', `${next === 'left' ? -dx : dx}px`);
    }
  }

  destroy() { this.root?.remove(); }
}

// ---------- styles ----------

let _stylesInjected = false;
function _ensureStyles() {
  if (_stylesInjected) return;
  _stylesInjected = true;
  const css = `
    .radial { position: absolute; inset: 0; pointer-events: none; z-index: 700; }
    .radial-backdrop {
      position: absolute; inset: 0; background: rgba(20,8,24,0.28);
      opacity: 0; pointer-events: none; transition: opacity 0.25s ease;
    }
    .radial.radial-open .radial-backdrop { opacity: 1; pointer-events: auto; }

    /* Left-handed: dock the hub, item origin and captions on the left edge.
       Items are mirrored in JS (setSide), so only the anchoring flips here. */
    .radial-left .radial-hub,
    .radial-left .radial-items { right: auto; left: 12px; }
    .radial-left .radial-caption { right: auto; left: 70px; }

    .radial-hub {
      position: absolute; right: 12px; top: 50%;
      width: ${HUB_SIZE}px; height: ${HUB_SIZE}px; margin-top: ${-HUB_SIZE / 2}px;
      border: none; border-radius: 50%;
      background: linear-gradient(150deg, #8B008B, #a8259c);
      color: #fff; font-size: 20px; cursor: pointer; pointer-events: auto;
      box-shadow: 0 4px 14px rgba(139,0,139,0.4);
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s;
      -webkit-tap-highlight-color: transparent;
    }
    .radial-hub:hover { box-shadow: 0 6px 18px rgba(139,0,139,0.5); }
    /* When the hub shows a brand image, let it fill the circle (no gradient). */
    .radial-hub-hasimg { background: #fff; padding: 0; overflow: hidden; }
    .radial-hub-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block; }
    .radial.radial-open .radial-hub { transform: rotate(90deg); background: linear-gradient(150deg,#6d006d,#8B008B); }
    .radial.radial-open .radial-hub-hasimg { background: linear-gradient(150deg,#6d006d,#8B008B); }
    .radial-hub .radial-hub-close { display: none; }
    .radial.radial-open .radial-hub .radial-hub-open { display: none; }
    .radial.radial-open .radial-hub .radial-hub-close { display: inline; }

    .radial-items {
      position: absolute; right: 12px; top: 50%;
      width: ${HUB_SIZE}px; height: ${HUB_SIZE}px; margin-top: ${-HUB_SIZE / 2}px;
      pointer-events: none;
    }
    .radial-item {
      position: absolute; left: 50%; top: 50%;
      width: ${ITEM_SIZE}px; height: ${ITEM_SIZE}px;
      margin-left: ${-ITEM_SIZE / 2}px; margin-top: ${-ITEM_SIZE / 2}px;
      border: none; background: transparent; cursor: pointer;
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
      transform: translate(0,0) scale(0.3); opacity: 0; pointer-events: none;
      transition: transform 0.28s cubic-bezier(0.22,1,0.36,1), opacity 0.2s ease;
      -webkit-tap-highlight-color: transparent;
    }
    .radial.radial-open .radial-item {
      transform: translate(var(--tx), var(--ty)) scale(1);
      opacity: 1; pointer-events: auto;
    }
    .radial-item-icon {
      width: 44px; height: 44px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: #fff; color: var(--accent, #8B008B);
      border: 2px solid var(--accent, #8B008B);
      font-size: 18px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
      transition: background 0.15s, color 0.15s;
    }
    .radial-item:hover .radial-item-icon { background: var(--accent, #8B008B); color: #fff; }
    .radial-item-active .radial-item-icon {
      background: #d32f2f; color: #fff; border-color: #d32f2f;
      animation: radial-pulse 1.3s ease-out infinite;
    }
    .radial-item-label {
      font-size: 9.5px; font-weight: 700; color: #333; line-height: 1.35;
      background: rgba(255,255,255,0.96); border-radius: 5px; padding: 2px 6px 3px;
      max-width: 72px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      box-shadow: 0 1px 2px rgba(0,0,0,0.12);
      font-family: -apple-system, 'Segoe UI', sans-serif;
    }
    @keyframes radial-pulse {
      0% { box-shadow: 0 0 0 0 rgba(211,47,47,0.55); }
      70% { box-shadow: 0 0 0 9px rgba(211,47,47,0); }
      100% { box-shadow: 0 0 0 0 rgba(211,47,47,0); }
    }

    .radial-caption {
      position: absolute; right: 70px;
      font-size: 8px; font-weight: 800; letter-spacing: 0.08em;
      opacity: 0; transition: opacity 0.25s ease 0.05s; pointer-events: none;
      font-family: -apple-system, 'Segoe UI', sans-serif;
    }
    .radial.radial-open .radial-caption { opacity: 0.85; }
    .radial-caption-upper { top: calc(50% - 166px); color: #556B2F; }
    .radial-caption-lower { top: calc(50% + 154px); color: #8B008B; }
  `;
  const el = document.createElement('style');
  el.id = 'radial-menu-styles';
  el.textContent = css;
  document.head.appendChild(el);
}

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export default RadialMenu;
